import { DispatchHelpMessage, DispatchHelpSession, DispatchHelpTakeover } from '@numeo/types'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface DispatchHelpState {
    sessions: Record<string, DispatchHelpSession>
    // Display order (newest activity first) — kept alongside the map so the list is stable.
    sessionOrder: string[]
    activeSessionId: string | null
    messagesByTab: Record<string, DispatchHelpMessage[]>
    unreadByTab: Record<string, number>
    // Read high-water mark per tab: ISO timestamp of the newest message the mobile owner has seen.
    // Any dispatcher bubble at/older than it renders ✓✓ (read); newer ones ✓ (sent). Seeded from the
    // session fetch (survives a late open) and advanced live by AH_ReadReceiptsUpdated.
    readMarkByTab: Record<string, string>
    loadingSessions: boolean
    loadingMessages: boolean
    // Thread paging, per tab. The server returns the NEWEST page first, so "next page" walks BACKWARDS
    // through the conversation: `messagesPageByTab` is the oldest page fetched so far. Without this a
    // long thread showed its last 50 messages and scrolling up hit a false top.
    messagesPageByTab: Record<string, number>
    hasOlderMessagesByTab: Record<string, boolean>
    // Separate from `loadingMessages` so fetching older turns never blanks the thread being read.
    loadingOlderMessages: boolean
    // Keyset paging for the session list: cursor for the next (older) page, and whether one exists.
    // `loadingMoreSessions` is separate from `loadingSessions` so a "load more" doesn't blank the list.
    sessionsCursor: string | null
    hasMoreSessions: boolean
    loadingMoreSessions: boolean
    // Tabs that came from a list fetch (first page or a paged-in older page) rather than live fan-out.
    // These are exempt from eviction — see evictLiveOverflow.
    listedTabs: Record<string, true>
    // What the agent is doing on a tab RIGHT NOW — the operator's equivalent of the phone's
    // searching/scoring animation. Transient: set by the live pipeline beats, cleared the moment the
    // agent's reply lands (see ingestLiveMessage), so it can never outlive the turn it describes.
    agentActivityByTab: Record<string, { message: string; at: number }>
    // Whether this admin is on the Gluon-managed operator allowlist. null = not yet checked.
    isOperator: boolean | null
}

// Cross-tenant fan-out means an always-connected operator would otherwise accumulate a session (and,
// once opened, a message array) per driver conversation for the whole shift. Only LIVE-arrived
// sessions are capped: a count-based cap over the whole list evicts oldest-activity-first, which is
// exactly the rows a "load older" just fetched, so one new session cross-tenant would silently undo
// the operator's paging.
const MAX_LIVE_SESSIONS = 200

const initialState: DispatchHelpState = {
    sessions: {},
    sessionOrder: [],
    activeSessionId: null,
    messagesByTab: {},
    unreadByTab: {},
    readMarkByTab: {},
    loadingSessions: false,
    loadingMessages: false,
    messagesPageByTab: {},
    hasOlderMessagesByTab: {},
    loadingOlderMessages: false,
    sessionsCursor: null,
    hasMoreSessions: false,
    loadingMoreSessions: false,
    listedTabs: {},
    agentActivityByTab: {},
    isOperator: null,
}

/** One keyset page of sessions as the reducers take it (the API's `SessionsPage`, flattened). */
interface SessionsPagePayload {
    sessions: DispatchHelpSession[]
    hasMore: boolean
    nextCursor: string | null
}

// Newest-activity-first ordering key.
function activityKey(s: DispatchHelpSession): number {
    return new Date(s.lastActivityAt ?? s.createdAt).getTime()
}

function reorder(state: DispatchHelpState): void {
    state.sessionOrder = Object.values(state.sessions)
        .sort((a, b) => activityKey(b) - activityKey(a))
        .map((s) => s.tabId)
}

// Advance a tab's read high-water mark — monotonic, so a stale/out-of-order update can't rewind the
// ticks (ISO-8601 UTC strings sort lexicographically in time order).
function advanceReadMark(state: DispatchHelpState, tabId: string, at?: string | null): void {
    if (!at) return
    const current = state.readMarkByTab[tabId]
    if (!current || at > current) state.readMarkByTab[tabId] = at
}

function evictLiveOverflow(state: DispatchHelpState): void {
    // sessionOrder is newest-first, so the tail of the live-only subset is the stalest fan-out.
    const live = state.sessionOrder.filter((id) => !state.listedTabs[id])
    if (live.length <= MAX_LIVE_SESSIONS) return
    for (const id of live.slice(MAX_LIVE_SESSIONS)) {
        if (id === state.activeSessionId) continue
        delete state.sessions[id]
        delete state.messagesByTab[id]
        delete state.messagesPageByTab[id]
        delete state.hasOlderMessagesByTab[id]
        delete state.unreadByTab[id]
        delete state.readMarkByTab[id]
    }
    reorder(state)
}

const dispatchHelpSlice = createSlice({
    name: 'dispatchHelp',
    initialState,
    reducers: {
        // First page (mount / filter change) — REPLACES the list and resets the keyset cursor.
        setSessions(state, action: PayloadAction<SessionsPagePayload>) {
            const { sessions, hasMore, nextCursor } = action.payload
            // Keep the OPEN session's row across the swap. It may not be in the refreshed page — a deep
            // link lands on one older than the first page, and a search the open thread doesn't match
            // filters it out — and dropping it blanks the thread the user is reading.
            const open = state.activeSessionId ? state.sessions[state.activeSessionId] : undefined
            state.sessions = open && state.activeSessionId ? { [state.activeSessionId]: open } : {}
            state.listedTabs = {}
            for (const s of sessions) {
                state.sessions[s.tabId] = s
                state.listedTabs[s.tabId] = true
                advanceReadMark(state, s.tabId, s.lastReadAt)
            }
            // Prune read marks for tabs no longer in the refreshed list so the map stays bounded to it.
            const present = new Set(sessions.map((s) => s.tabId))
            if (state.activeSessionId) present.add(state.activeSessionId)
            for (const id of Object.keys(state.readMarkByTab)) if (!present.has(id)) delete state.readMarkByTab[id]
            state.sessionsCursor = nextCursor
            state.hasMoreSessions = hasMore
            reorder(state)
        },
        // Next (older) page — MERGES, so live rows appended since the first fetch survive. Read marks
        // are not pruned here: the payload is a slice of the list, not the whole of it.
        appendSessions(state, action: PayloadAction<SessionsPagePayload>) {
            const { sessions, hasMore, nextCursor } = action.payload
            for (const s of sessions) {
                // A session bumped by live activity between pages can repeat across the keyset window.
                // The local copy is the newer one, so leave it alone and only fill in genuinely new rows.
                if (!state.sessions[s.tabId]) state.sessions[s.tabId] = s
                state.listedTabs[s.tabId] = true
                advanceReadMark(state, s.tabId, s.lastReadAt)
            }
            state.sessionsCursor = nextCursor
            state.hasMoreSessions = hasMore
            reorder(state)
        },
        // Filter changed — the cursor still points into the PREVIOUS filter's result set, so drop it
        // before the debounced refetch. Load-more is cursor-gated, which parks it until the new first
        // page lands rather than letting it append the new filter's rows off the old position.
        resetSessionsWindow(state) {
            state.sessionsCursor = null
            state.hasMoreSessions = false
        },
        upsertSession(state, action: PayloadAction<DispatchHelpSession>) {
            const s = action.payload
            state.sessions[s.tabId] = { ...state.sessions[s.tabId], ...s }
            advanceReadMark(state, s.tabId, s.lastReadAt)
            reorder(state)
            evictLiveOverflow(state)
        },
        setActiveSession(state, action: PayloadAction<string | null>) {
            state.activeSessionId = action.payload
            if (action.payload) state.unreadByTab[action.payload] = 0
        },
        setIsOperator(state, action: PayloadAction<boolean>) {
            state.isOperator = action.payload
        },
        setMessages(state, action: PayloadAction<{ tabId: string; messages: DispatchHelpMessage[]; total?: number }>) {
            const { tabId, messages, total } = action.payload
            state.messagesByTab[tabId] = messages
            state.messagesPageByTab[tabId] = 1
            // `total` is the whole thread server-side; more than we hold means older pages are waiting.
            state.hasOlderMessagesByTab[tabId] = total != null && total > messages.length
        },
        // An older page, fetched when the operator scrolls to the top. Prepends and de-dupes by id: a
        // live row can already be in the thread, and the page boundary can repeat one.
        prependOlderMessages(state, action: PayloadAction<{ tabId: string; messages: DispatchHelpMessage[]; page: number; total: number }>) {
            const { tabId, messages, page, total } = action.payload
            const byId = new Map<string, DispatchHelpMessage>()
            for (const m of messages) byId.set(m.id, m)
            for (const m of state.messagesByTab[tabId] ?? []) byId.set(m.id, { ...byId.get(m.id), ...m })
            const merged = Array.from(byId.values()).sort((a, b) => a.createdAt.localeCompare(b.createdAt))
            state.messagesByTab[tabId] = merged
            state.messagesPageByTab[tabId] = page
            state.hasOlderMessagesByTab[tabId] = merged.length < total
        },
        setLoadingOlderMessages(state, action: PayloadAction<boolean>) {
            state.loadingOlderMessages = action.payload
        },
        // Merge a fetched snapshot into the existing thread instead of replacing it — used by the
        // staggered reply-refetch so a slow/out-of-order fetch can't clobber newer live-appended rows
        // (or an operator's just-sent optimistic reply). Server rows win on id collision, but fields the
        // server doesn't persist (wantsDispatcher / replyTo — live-only) are kept from the local row.
        // Anything present only locally (a live row the read replica hasn't caught up on) survives too.
        mergeMessages(state, action: PayloadAction<{ tabId: string; messages: DispatchHelpMessage[] }>) {
            const { tabId, messages } = action.payload
            const byId = new Map<string, DispatchHelpMessage>()
            for (const m of state.messagesByTab[tabId] ?? []) byId.set(m.id, m)
            for (const m of messages) byId.set(m.id, { ...byId.get(m.id), ...m }) // server wins, but keep live-only fields
            state.messagesByTab[tabId] = Array.from(byId.values()).sort((a, b) => a.createdAt.localeCompare(b.createdAt))
        },
        removeMessage(state, action: PayloadAction<{ tabId: string; id: string }>) {
            const list = state.messagesByTab[action.payload.tabId]
            if (list) state.messagesByTab[action.payload.tabId] = list.filter((m) => m.id !== action.payload.id)
        },
        appendMessage(state, action: PayloadAction<{ tabId: string; message: DispatchHelpMessage }>) {
            const { tabId, message } = action.payload
            const list = state.messagesByTab[tabId] ?? (state.messagesByTab[tabId] = [])
            if (list.some((m) => m.id === message.id)) return // dedupe (optimistic + echo + refetch)
            list.push(message)
        },
        // Live cross-tenant message fan-out: append ONLY to a thread the operator has already opened
        // (or the active one). For unopened sessions we skip the body — the list still bumps unread via
        // touchActivity/bumpUnread, and the full thread is fetched on open — so messagesByTab can't grow
        // to one array per driver conversation platform-wide.
        ingestLiveMessage(state, action: PayloadAction<{ tabId: string; message: DispatchHelpMessage }>) {
            const { tabId, message } = action.payload
            // The agent finished talking → whatever it was doing is over, even if the thread isn't open.
            if (message.role === 'agent' && message.text) delete state.agentActivityByTab[tabId]
            const list = state.messagesByTab[tabId]
            if (!list) return
            if (list.some((m) => m.id === message.id)) return
            list.push(message)
        },
        // A turn started or moved on ('Working…', 'Scoring 120 loads with AI…'). Empty message clears
        // it — a finished turn should leave no spinner behind.
        setAgentActivity(state, action: PayloadAction<{ tabId: string; message: string }>) {
            const { tabId, message } = action.payload
            if (message) state.agentActivityByTab[tabId] = { message, at: Date.now() }
            else delete state.agentActivityByTab[tabId]
        },
        // Swap an optimistic (local-*) row for its persisted server row once the POST returns,
        // so the dispatcher's own message isn't rendered twice (temp id + real id are distinct,
        // so appendMessage's id-dedupe can't catch it). Falls back to append if the temp is gone.
        reconcileMessage(state, action: PayloadAction<{ tabId: string; tempId: string; message: DispatchHelpMessage }>) {
            const { tabId, tempId, message } = action.payload
            const list = state.messagesByTab[tabId] ?? (state.messagesByTab[tabId] = [])
            if (list.some((m) => m.id === message.id)) {
                // Real row already arrived (socket echo) — just drop the optimistic placeholder.
                state.messagesByTab[tabId] = list.filter((m) => m.id !== tempId)
                return
            }
            const idx = list.findIndex((m) => m.id === tempId)
            if (idx === -1) list.push(message)
            else list[idx] = message
        },
        setTakeover(state, action: PayloadAction<{ tabId: string; takeover: DispatchHelpTakeover | null }>) {
            const session = state.sessions[action.payload.tabId]
            if (session) {
                session.takeover = action.payload.takeover
                // A takeover answers the raised hand — drop it.
                if (action.payload.takeover) session.helpRequested = false
            }
        },
        setHelpRequested(state, action: PayloadAction<{ tabId: string; helpRequested: boolean }>) {
            const session = state.sessions[action.payload.tabId]
            if (session) session.helpRequested = action.payload.helpRequested
        },
        touchActivity(state, action: PayloadAction<{ tabId: string; lastActivityAt?: string }>) {
            const session = state.sessions[action.payload.tabId]
            if (session && action.payload.lastActivityAt) {
                session.lastActivityAt = action.payload.lastActivityAt
                reorder(state)
            }
        },
        bumpUnread(state, action: PayloadAction<string>) {
            const tabId = action.payload
            if (tabId === state.activeSessionId) return
            state.unreadByTab[tabId] = (state.unreadByTab[tabId] ?? 0) + 1
        },
        markRead(state, action: PayloadAction<string>) {
            state.unreadByTab[action.payload] = 0
        },
        // The mobile owner read up to `lastReadAt` (AH_ReadReceiptsUpdated) — advance the read tick mark.
        setReadMark(state, action: PayloadAction<{ tabId: string; lastReadAt: string }>) {
            advanceReadMark(state, action.payload.tabId, action.payload.lastReadAt)
        },
        setLoadingSessions(state, action: PayloadAction<boolean>) {
            state.loadingSessions = action.payload
        },
        setLoadingMoreSessions(state, action: PayloadAction<boolean>) {
            state.loadingMoreSessions = action.payload
        },
        setLoadingMessages(state, action: PayloadAction<boolean>) {
            state.loadingMessages = action.payload
        },
    },
})

export const {
    setSessions,
    appendSessions,
    resetSessionsWindow,
    upsertSession,
    setActiveSession,
    setIsOperator,
    setMessages,
    prependOlderMessages,
    setLoadingOlderMessages,
    mergeMessages,
    removeMessage,
    appendMessage,
    ingestLiveMessage,
    setAgentActivity,
    reconcileMessage,
    setTakeover,
    setHelpRequested,
    touchActivity,
    bumpUnread,
    markRead,
    setReadMark,
    setLoadingSessions,
    setLoadingMoreSessions,
    setLoadingMessages,
} = dispatchHelpSlice.actions

export default dispatchHelpSlice.reducer
